import React from 'react';
import { Pencil, Trash2 } from 'lucide-react';
import { AppButton } from '../ui/AppButton';
import { SidePanel } from '../ui/SidePanel';
import { BandLabel, EPCBadge } from './BuildingBadges';

const Fact = ({ label, children }) => (
  <div className="min-w-0">
    <dt className="text-[12px] text-lb-muted">{label}</dt>
    <dd className="m-0 mt-0.5 text-[14px] text-lb-ink-strong break-words">
      {children != null && children !== '' ? children : '—'}
    </dd>
  </div>
);

/**
 * Details of one building in a side panel. `onEdit` / `onDelete` are
 * omitted when the user may not use them, which hides those buttons.
 */
export const BuildingPanel = ({ building: b, onClose, onEdit, onDelete }) => (
  <SidePanel
    open={Boolean(b)}
    onClose={onClose}
    title={b?.name}
    subtitle={b && (
      <div className="flex flex-col gap-3">
        <p className="m-0 text-[13px] text-lb-muted">{b.displayAddress || 'Address not set'}</p>
        <div className="flex items-center gap-2.5 flex-wrap">
          <EPCBadge rating={b.epc} />
          <span className="text-[11px] text-lb-muted" title="Estimated from the performance score until a certified EPC is available">est.</span>
          {b.performance != null && <BandLabel score={b.performanceScore} />}
        </div>
      </div>
    )}
    footer={b && (onEdit || onDelete) && (
      <>
        {onEdit
          ? <AppButton variant="primary" icon={<Pencil size={14} />} onClick={() => onEdit(b)}>Edit building</AppButton>
          : <span />}
        {onDelete && (
          <AppButton variant="ghost" icon={<Trash2 size={14} />} className="!text-[#B85052]" onClick={() => onDelete(b)}>
            Delete
          </AppButton>
        )}
      </>
    )}
  >
    {b && (
      <>
        <h3 className="m-0 mb-3 text-[12px] font-semibold text-lb-muted uppercase tracking-[0.04em]">Details</h3>
        <dl className="m-0 grid grid-cols-2 gap-x-6 gap-y-4">
          <Fact label="Type">{b.building_type || b.type}</Fact>
          <Fact label="Year built">{b.year_built}</Fact>
          <Fact label="Floor area">{b.total_area ? `${Number(b.total_area).toLocaleString()} ${b.total_area_unit || 'm²'}` : null}</Fact>
          <Fact label="Floors">{b.number_of_floors}</Fact>
          <Fact label="Occupancy">{b.occupancy != null && b.occupancy !== '' ? `${b.occupancy}%` : null}</Fact>
          <Fact label="Coordinates">{b.hasCoords ? `${b.lat.toFixed(5)}, ${b.lng.toFixed(5)}` : null}</Fact>
        </dl>
      </>
    )}
  </SidePanel>
);
